import React from 'react';
import { NameEmailAvatar, TableColumn } from '../../../../../ui';
import { ContextMenuButton } from '../../../../../ui/components/Common/ContextMenu/ContextMenuButton';
import { TrashIcon } from '../../../../../ui/icons/icons';
import { TooltipWrapper } from '../../../../job_openings/view/Functions';

export interface BillingHistory {
  id: string;
  date: string;
  amount: string;
  status: string;
  method: string;
}

export interface OrgUser {
  id: number;
  name: string;
  email: string;
  role: string;
  status: string;
  joined: string;
}

export const getColumns = (
  handleDownload: (id: string) => void,
): TableColumn<BillingHistory>[] => [
  {
    key: 'id',
    label: 'Invoice',
    width: '120px',
    align: 'left' as const,
    render: (row: BillingHistory) => <TooltipWrapper text={row.id} />,
    disableSort: false,
  },
  {
    key: 'date',
    label: 'Date',
    width: '120px',
    align: 'left' as const,
    render: (row: BillingHistory) => <TooltipWrapper text={row.date} />,
    disableSort: true,
  },
  {
    key: 'amount',
    label: 'Amount',
    width: '120px',
    align: 'left' as const,
    render: (row: BillingHistory) => <TooltipWrapper text={row.amount} />,
  },
  {
    key: 'status',
    label: 'Status',
    width: '100px',
    align: 'left' as const,
    render: (row: BillingHistory) => (
      <div className='flex justify-start text-left '>
        <span className='px-3 py-1 bg-emerald-50 text-emerald-600 rounded-full text-[10px] font-black uppercase tracking-widest'>
          { row.status }
        </span>
      </div>
    ),
  },
  {
    key: 'method',
    label: 'Method',
    width: '150px',
    align: 'left' as const,
    render: (row: BillingHistory) => <TooltipWrapper text={row.method} />,
  },
  {
    key: 'actions',
    label: 'Actions',
    width: '100px',
    align: 'right',
    render: (row: BillingHistory) => (
      <div className='flex justify-end text-left '>
        <button
            onClick={() => handleDownload(row.id)}
            className='text-emerald-600 font-bold text-xs hover:underline'
        >
          Download PDF
        </button>
      </div>
    ),
  },
];

export const getUserColumns = (
  onOpenMenu: (e: React.MouseEvent<HTMLButtonElement>, row: unknown) => void,
  onCloseMenu: () => void,
  anchorEl: HTMLElement | null,
  selectedUser: OrgUser | null,
): TableColumn<OrgUser>[] => [
  {
    key: 'name',
    label: 'User',
    width: '250px',
    align: 'left' as const,
    render: (row: OrgUser) => (
      <div className='flex items-center gap-3'>
        <NameEmailAvatar avatar='' name={row.name} email={row.email} />
      </div>
    ),
    disableSort: true,
  },
  {
    key: 'role',
    label: 'Role',
    width: '120px',
    align: 'left' as const,
    render: (row: OrgUser) => (
      <div className='flex justify-start text-left '>
        <span className='px-3 py-1 bg-slate-100 text-slate-600 rounded-full text-[10px] font-black uppercase tracking-widest'>
          { row.role }
        </span>
      </div>
    ),
    disableSort: true,
  },
  {
    key: 'status',
    label: 'Status',
    width: '120px',
    align: 'left' as const,
    render: (row: OrgUser) => (
      <div className='flex items-center gap-1.5'>
        <div
            className={`w-1.5 h-1.5 rounded-full ${
              row.status === 'Active'
                ? 'bg-emerald-500 shadow-[0_0_10px_rgba(16,185,129,0.5)]'
                : 'bg-slate-300'
            }`}
        />
        <span
            className={`text-[10px] font-black uppercase tracking-widest ${
              row.status === 'Active' ? 'text-emerald-600' : 'text-slate-400'
            }`}
        >
          { row.status }
        </span>
      </div>
    ),
    disableSort: true,
  },
  {
    key: 'joined',
    label: 'Joined',
    width: '120px',
    align: 'left' as const,
    render: (row: OrgUser) => <TooltipWrapper text={row.joined} />,
    disableSort: true,
  },
  {
    key: 'actions',
    label: 'Actions',
    width: '80px',
    align: 'right',
    render: (row: OrgUser) => (
      <div className='flex justify-end pr-2'>
        <ContextMenuButton
            row={row}
            onOpenMenu={onOpenMenu}
            onCloseMenu={onCloseMenu}
            anchorEl={selectedUser?.id === row.id ? anchorEl : null}
        />
      </div>
    ),
    disableSort: true,
  },
];

export interface SecurityLog {
  id: number;
  action: string;
  user: string;
  ip: string;
  timestamp: string;
}

export const getSecurityLogColumns = (): TableColumn<SecurityLog>[] => [
  {
    key: 'action',
    label: 'Action',
    width: '250px',
    align: 'left' as const,
    render: (row: SecurityLog) => <TooltipWrapper text={row.action} />,
  },
  {
    key: 'user',
    label: 'User',
    width: '150px',
    align: 'left' as const,
    render: (row: SecurityLog) => <TooltipWrapper text={row.user} />,
    disableSort: true,
  },
  {
    key: 'ip',
    label: 'IP Address',
    width: '150px',
    align: 'left' as const,
    render: (row: SecurityLog) => <TooltipWrapper text={row.ip} />,
    disableSort: true,
  },
  {
    key: 'timestamp',
    label: 'Timestamp',
    width: '200px',
    align: 'left' as const,
    render: (row: SecurityLog) => <TooltipWrapper text={row.timestamp} />,
    disableSort: true,
  },

];
